import { types, Instance, flow, getRoot } from 'mobx-state-tree';

import {
  ApiWorkout,
  GuidedPhases,
  CreateWorkoutPayload,
  RemoteConfigData,
} from '@/stores/workoutPlayer/types';
import type {
  IGuidedWorkoutModel,
  IUserSegmentChapterModel,
  IGuidedWorkoutSegmentModel,
} from '@/stores/models/GuidedWorkoutModel';
import {
  startWorkout as apiStartWorkout,
  updateSegment as apiUpdateSegment,
  completeWorkout as apiCompleteWorkout,
  createWorkout as apiCreateWorkout,
  updateUserSegmentChapter as apiUpdateChapter,
} from '@/services/api/workoutApi';
import { resolveGuidedWorkout } from '@/services/workoutResolver';
import remoteConfigData from '@/data/remoteConfig';
import {
  downloadVideo as dlVideo,
  getCachedVideoUri,
  deleteCachedVideo,
} from '@/services/videoDownload';
import type { DownloadHandle } from '@/services/videoDownload.types';
import {
  ensureSpaceForVideo,
  isOutOfSpaceError,
} from '@/services/videoStorage';
import { verifyVideoFile } from '@/services/videoIntegrity';
import { nowMs } from '@/services/datetime';
import throttle from 'lodash/throttle';
import type { DebouncedFunc } from 'lodash';

const GUIDED_PHASE_KEYS = Object.keys(GuidedPhases) as GuidedPhases[];

// How long to keep displaying the intended seek position before giving up and
// falling back to the player's real position. Longer than any normal seek so a
// slow-but-working seek is never abandoned (which would reintroduce a flicker);
// short enough that a dropped seek can't strand the UI on a chapter the player
// never reached. Checked on timeUpdate (~1s cadence), so it effectively rounds
// up to the next event.
const SEEK_SETTLE_TIMEOUT_MS = 3000;

// Coalesce bursty backend writes from rapid chapter skipping (which can hit API
// rate limits). lodash throttle defaults to leading+trailing: the leading edge
// persists the first change immediately, the trailing edge persists the final
// settled one, and the rapid-fire writes in between — which carry ~no real
// progress — are dropped. Flushed at completion and teardown so nothing is
// lost. ~Matches the mobile app's 1500ms throttle.
const BACKEND_WRITE_THROTTLE_MS = 1500;

export type IGuidedWorkoutPlayerStore = Instance<
  typeof GuidedWorkoutPlayerStore
>;

const GuidedWorkoutPlayerStore = types
  .model('GuidedWorkoutPlayerStore', {
    phase: types.optional(
      types.enumeration('GuidedPhase', GUIDED_PHASE_KEYS),
      GuidedPhases.IDLE,
    ),
    isPaused: false,
    // Raw playback position reported by the player (expo-video timeUpdate).
    // The chapter index and every time/progress view derive from the DISPLAYED
    // time (the `currentTimeMs` view), which equals this value unless a seek is
    // in flight — see `seekTargetMs`.
    observedTimeMs: 0,

    workoutStartedAt: types.maybeNull(types.number),
    workoutCompletedAt: types.maybeNull(types.number),
  })
  .volatile(() => ({
    workout: null as IGuidedWorkoutModel | null,
    isCreatingWorkout: false,
    createError: null as string | null,
    downloadProgress: 0,
    downloadHandle: null as DownloadHandle | null,
    playbackUri: null as string | null,
    isStreaming: false,
    storageFull: false,
    // One-shot signal to the controller to issue a native seek (skip/restart).
    // The controller's reaction sets player.currentTime and then clears it.
    pendingSeekMs: null as number | null,
    // Intended playback position while a skip/restart seek is in flight — the
    // heart of the no-flicker model. The UI renders the INTENDED position (this
    // value, via the `currentTimeMs` view) rather than the player's OBSERVED
    // position until the player actually arrives at the intended chapter. So a
    // late timeUpdate carrying the PRE-seek position (native seeks have real
    // latency on-device) is never shown and can't revert the chapter/time/
    // progress — it isn't what we display, and it can't release the intent
    // until the observed position reaches the target chapter. Null when no seek
    // is pending, in which case the UI follows the observed position directly.
    seekTargetMs: null as number | null,
    // Wall-clock time the in-flight seek was issued, paired with seekTargetMs.
    // Used only to time out a seek that never lands (see SEEK_SETTLE_TIMEOUT_MS).
    seekStartedAtMs: null as number | null,
    // The chapter already reconciled with the backend (progress save + current-
    // chapter update). Natural boundary crossings compare against this; skip and
    // restart set it themselves so arrival doesn't double-fire a save.
    lastAccountedChapter: 0,
    // Floor for playback position on cold-start resume. Mobile-style:
    // until reported currentTime catches up, the controller forces the
    // player forward to this position on each timeUpdate. Cleared once
    // playback has caught up.
    initialSeekMs: null as number | null,
    // Throttled backend writers, created in afterCreate. The skip path and
    // natural chapter advances route through these so they can't spam the API;
    // they are flushed in endWorkout/reset so a pending write is never lost.
    _throttledChapterProgress: null as DebouncedFunc<
      (chapterId: number, progressMs: number) => void
    > | null,
    _throttledCurrentChapter: null as DebouncedFunc<
      (chapterId: number | null) => void
    > | null,
  }))
  .views((self) => ({
    get segment(): IGuidedWorkoutSegmentModel | null {
      return self.workout?.segment ?? null;
    },

    get chapters(): IUserSegmentChapterModel[] {
      return (self.workout?.chapters as IUserSegmentChapterModel[]) ?? [];
    },

    /**
     * The time the UI displays. While a skip/restart seek is in flight this is
     * the intended position, so the chapter/time/progress jump straight to the
     * destination and stay there — never flashing the pre-seek position that a
     * late timeUpdate would otherwise carry. Otherwise it's the observed time.
     */
    get currentTimeMs(): number {
      return self.seekTargetMs ?? self.observedTimeMs;
    },

    /** Derived from the displayed time — single source of truth, so the chapter
     * can never disagree with the time/progress shown alongside it. */
    get currentChapterIndex(): number {
      return (
        self.workout?.segment.findChapterIndexAtTime(this.currentTimeMs) ?? 0
      );
    },

    get currentChapter(): IUserSegmentChapterModel | null {
      const chs = this.chapters;
      const idx = this.currentChapterIndex;
      if (idx < 0 || idx >= chs.length) {
        return null;
      }
      return chs[idx];
    },

    get currentChapterTitle(): string {
      return this.currentChapter?.title ?? '';
    },

    get videoUrl(): string | null {
      return self.workout?.videoUrl ?? null;
    },

    get videoHash(): string | null {
      return self.workout?.videoHash ?? null;
    },

    get videoSize(): number | null {
      return self.workout?.videoSize ?? null;
    },

    get chapterProgressLabel(): string {
      const total = this.chapters.length;
      if (total === 0) return '';
      return `Chapter ${this.currentChapterIndex + 1}/${total}`;
    },

    get totalDurationMs(): number {
      return self.workout?.totalDurationMs ?? 0;
    },

    get timeLabel(): string {
      const current = this.currentTimeMs;
      const total = this.totalDurationMs;
      return `${formatTime(current)} / ${formatTime(total)}`;
    },

    get overallProgress(): number {
      const total = this.totalDurationMs;
      if (total <= 0) return 0;
      return Math.min(this.currentTimeMs / total, 1);
    },

    get chapterProgress(): number {
      const ch = this.currentChapter;
      if (!ch) return 0;
      const duration = ch.endTimeMs - ch.startTimeMs;
      if (duration <= 0) return 0;
      return Math.min(
        Math.max((this.currentTimeMs - ch.startTimeMs) / duration, 0),
        1,
      );
    },

    get isDownloading(): boolean {
      return self.phase === GuidedPhases.DOWNLOADING;
    },

    get isReady(): boolean {
      return self.phase === GuidedPhases.READY;
    },

    get isFinished(): boolean {
      return self.phase === GuidedPhases.FINISHED;
    },

    get isPlaying(): boolean {
      return self.phase === GuidedPhases.PLAYING;
    },

    get playbackVideoUri(): string | null {
      return self.playbackUri;
    },

    get isLastChapter(): boolean {
      return this.currentChapterIndex >= this.chapters.length - 1;
    },

    get chapterMarkers(): { position: number; title: string }[] {
      return self.workout?.segment.chapterMarkers ?? [];
    },
  }))
  .actions((self) => {
    /** Send chapter progress to backend — only called on chapter completion or skip */
    function updateChapterProgress(chapterId: number, progressMs: number) {
      if (!self.workout) return;
      apiUpdateChapter(self.workout.id, self.workout.segment.id, chapterId, {
        total_progress_time_ms: progressMs,
      });
    }

    /** Send current_user_chapter_id to backend */
    function updateSegmentCurrentChapter(chapterId: number | null) {
      if (!self.workout) return;
      apiUpdateSegment(self.workout.id, self.workout.segment.id, {
        started_at: self.workout.segment.started_at,
        current_user_chapter_id: chapterId,
      });
    }

    /**
     * Save progress for the chapter at the given index based on current playback time.
     * If the chapter completed naturally, sends its full duration.
     * If the user skipped, sends the partial progress within that chapter.
     */
    function saveChapterProgress(chapterIndex: number, wasCompleted: boolean) {
      const chapter = self.chapters[chapterIndex];
      if (!chapter) return;

      if (wasCompleted) {
        // Natural completion or final chapter — low-frequency, write directly.
        updateChapterProgress(chapter.id, chapter.durationMs);
      } else {
        // Partial progress from a user skip — throttled, since skipping can be
        // rapid. The leading edge captures the chapter you actually watched
        // before skipping; chapters blown through carry ~0 progress and are
        // safely coalesced away.
        const progressMs = Math.max(
          self.currentTimeMs - chapter.startTimeMs,
          0,
        );
        self._throttledChapterProgress?.(chapter.id, progressMs);
      }
    }

    /** Force any pending throttled backend writes out now. Called at lifecycle
     * boundaries (completion, teardown) so a coalesced write is never lost and
     * never fires after a terminal write. */
    function flushBackendWrites() {
      self._throttledChapterProgress?.flush();
      self._throttledCurrentChapter?.flush();
    }

    function endWorkout() {
      if (!self.workout || !self.workoutStartedAt) return;

      // Land any pending throttled skip writes before the terminal writes below,
      // so they can't fire out of order after the workout is marked complete.
      flushBackendWrites();

      self.workoutCompletedAt = nowMs();

      // Save final chapter progress (completed)
      saveChapterProgress(self.currentChapterIndex, true);

      // Complete the segment
      apiUpdateSegment(self.workout.id, self.workout.segment.id, {
        completed_at: self.workoutCompletedAt,
        started_at: self.workout.segment.started_at,
      });

      apiCompleteWorkout(
        self.workout.id,
        self.workoutStartedAt,
        self.workoutCompletedAt,
      );

      // Drop any cached paused entry for this template type — workout is done.
      const root = getRoot(self) as any;
      root.workoutSessionStore?.clearPaused?.(
        self.workout.workout_template_type,
      );

      self.phase = GuidedPhases.FINISHED;
    }

    /**
     * Whether an in-flight skip/restart seek has actually arrived. We wait for
     * the observed position to land IN the target chapter rather than merely
     * near the target time: native seeks are tolerant and snap to the nearest
     * keyframe at or before the target, so a time-based check could release the
     * intent while the player is still a hair inside the previous chapter and
     * briefly show it. Comparing chapters is robust in both directions (forward
     * skip, backward skip/restart). Caveat: a target chapter shorter than the
     * timeUpdate interval could be played through before any event lands in it —
     * not a concern for real workout chapters (seconds to minutes long).
     */
    function seekHasLanded(observedMs: number): boolean {
      const seg = self.workout?.segment;
      if (!seg || self.seekTargetMs == null) return true;
      return (
        seg.findChapterIndexAtTime(observedMs) ===
        seg.findChapterIndexAtTime(self.seekTargetMs)
      );
    }

    /**
     * Reconcile a NATURAL chapter advance (playback crossing a boundary on its
     * own) with the backend. Skip/restart account for their own transition by
     * setting `lastAccountedChapter`, so this is a no-op when they arrive.
     */
    function reconcileChapter() {
      const idx = self.currentChapterIndex;
      if (idx === self.lastAccountedChapter) return;

      console.log(
        `[GuidedStore] Chapter changed: ${self.lastAccountedChapter} → ${idx}`,
      );

      // A forward crossing means the chapter we left finished — save it whole.
      if (idx > self.lastAccountedChapter) {
        saveChapterProgress(self.lastAccountedChapter, true);
      }
      self.lastAccountedChapter = idx;
      // Throttled like the skip pointer write, so all in-playback current-chapter
      // updates serialize through one writer (last-wins, no reorder).
      self._throttledCurrentChapter?.(self.chapters[idx]?.id ?? null);
    }

    return {
      afterCreate() {
        // Create the throttled backend writers once per store instance. They
        // call the direct writers above, so all the workout/segment-id plumbing
        // and null-guards are reused.
        self._throttledChapterProgress = throttle(
          (chapterId: number, progressMs: number) =>
            updateChapterProgress(chapterId, progressMs),
          BACKEND_WRITE_THROTTLE_MS,
        );
        self._throttledCurrentChapter = throttle(
          (chapterId: number | null) => updateSegmentCurrentChapter(chapterId),
          BACKEND_WRITE_THROTTLE_MS,
        );
      },

      onTimeUpdate(observedMs: number) {
        if (self.phase !== GuidedPhases.PLAYING) return;

        // Track the player's real position. The UI never reads this directly —
        // it reads the DISPLAYED time (the currentTimeMs view), which is the
        // seek intent while a seek settles and the observed position otherwise.
        self.observedTimeMs = observedMs;

        // While a skip/restart seek is in flight, keep displaying the intended
        // position and ignore this update's effects until the player has truly
        // arrived in the target chapter; then release to follow the observed
        // position. This is what removes the on-device flicker: a stale pre-seek
        // timeUpdate is never displayed and never reverts the chapter.
        if (self.seekTargetMs != null) {
          const landed = seekHasLanded(observedMs);
          // Safety valve: if a seek never lands (e.g. the player silently
          // dropped it), stop pinning after a timeout and fall back to the real
          // position, so the UI can't stay frozen on a chapter the player never
          // reached. We check `landed` first, so a slow-but-successful seek
          // always takes the normal path no matter how long it took.
          const timedOut =
            !landed &&
            self.seekStartedAtMs != null &&
            nowMs() - self.seekStartedAtMs > SEEK_SETTLE_TIMEOUT_MS;
          if (!landed && !timedOut) return;
          if (timedOut) {
            console.warn(
              '[GuidedStore] Seek did not settle within timeout — releasing to observed position',
            );
          }
          self.seekTargetMs = null;
          self.seekStartedAtMs = null;
        }

        reconcileChapter();
      },

      onVideoEnd() {
        if (self.phase !== GuidedPhases.PLAYING) return;
        console.log('[GuidedStore] Video ended');
        endWorkout();
      },

      togglePause() {
        self.isPaused = !self.isPaused;
        console.log(`[GuidedStore] togglePause -> isPaused=${self.isPaused}`);
      },

      /**
       * Mirrors the player's actual play/pause state into the store. Used to
       * follow out-of-band changes — notably Android's hardware play/pause
       * button, which expo-video's Media3 MediaSession applies straight to the
       * player without going through the store. Idempotent.
       */
      setPaused(value: boolean) {
        if (self.isPaused === value) return;
        self.isPaused = value;
        console.log(`[GuidedStore] setPaused -> isPaused=${self.isPaused}`);
      },

      startPlayback() {
        if (!self.isPaused) {
          console.warn(
            '[GuidedStore] startPlayback: already playing — ignoring redundant call (possible duplicate countdown-complete)',
          );
          return;
        }
        self.isPaused = false;
        console.log('[GuidedStore] startPlayback -> isPaused=false');
      },

      /** Skips to the next chapter, or ends the workout if on the last chapter. */
      skipToNextChapter() {
        if (self.isLastChapter) {
          endWorkout();
          return;
        }

        // Partial save for the chapter we're leaving (skipped, not completed).
        // Reads the displayed time, which is still the real position here since
        // we haven't set the intent yet.
        saveChapterProgress(self.currentChapterIndex, false);

        const nextIndex = self.currentChapterIndex + 1;
        const nextChapter = self.chapters[nextIndex];
        const targetMs = nextChapter.startTimeMs;

        // Move the UI to the next chapter immediately (intent) and ask the
        // player to seek there. We've accounted for leaving the current chapter,
        // so mark the next as reconciled to avoid a double-save on arrival.
        self.seekTargetMs = targetMs;
        self.seekStartedAtMs = nowMs();
        self.lastAccountedChapter = nextIndex;
        self.pendingSeekMs = targetMs;

        self._throttledCurrentChapter?.(nextChapter.id);

        console.log(
          `[GuidedStore] Skipped to chapter ${nextIndex}: ${nextChapter.title}`,
        );
      },

      /** Restarts the current chapter from its start time. */
      restartCurrentChapter() {
        const idx = self.currentChapterIndex;
        const targetMs = self.chapters[idx]?.startTimeMs ?? 0;

        // Same chapter — no completion or accounting change, just pin the
        // display to the chapter start and seek there.
        self.seekTargetMs = targetMs;
        self.seekStartedAtMs = nowMs();
        self.pendingSeekMs = targetMs;

        console.log(`[GuidedStore] Restarted chapter ${idx}`);
      },

      clearPendingSeek() {
        self.pendingSeekMs = null;
      },

      clearInitialSeek() {
        self.initialSeekMs = null;
      },

      startWorkout() {
        if (!self.workout) {
          console.warn('[GuidedStore] Cannot start workout: no workout set');
          return;
        }
        if (self.phase !== GuidedPhases.READY) {
          console.warn(
            `[GuidedStore] Cannot start workout: phase is ${self.phase}, expected READY`,
          );
          return;
        }

        self.workoutStartedAt = nowMs();
        self.workout.segment.markStarted();
        self.observedTimeMs = 0;
        self.seekTargetMs = null;
        self.lastAccountedChapter = 0;

        apiStartWorkout(self.workout.id, self.workoutStartedAt);
        updateSegmentCurrentChapter(self.chapters[0]?.id ?? null);

        // Start paused — user must press play to begin
        self.isPaused = true;
        self.phase = GuidedPhases.PLAYING;
      },

      resumeWorkout() {
        if (!self.workout) {
          console.warn('[GuidedStore] Cannot resume workout: no workout set');
          return;
        }
        if (self.phase !== GuidedPhases.READY) {
          console.warn(
            `[GuidedStore] Cannot resume workout: phase is ${self.phase}, expected READY`,
          );
          return;
        }

        // Restart of the current chapter (partial in-chapter progress is lost
        // intentionally — matches mobile UX and avoids fragile timer rehydration).
        const chapter = self.chapters[self.currentChapterIndex];
        const targetMs = chapter?.startTimeMs ?? 0;
        self.observedTimeMs = targetMs;
        self.seekTargetMs = null;
        self.lastAccountedChapter = self.currentChapterIndex;
        // Use initialSeekMs (a floor enforced on timeUpdate) instead of
        // pendingSeekMs: the player isn't loaded yet at this point, and the
        // controller's pendingSeekMs reaction only fires on subsequent changes.
        self.initialSeekMs = targetMs;
        console.log(
          '[GuidedStore] resumeWorkout — initialSeekMs set to',
          targetMs,
          'for chapter',
          self.currentChapterIndex,
        );

        // Tell backend we're back on this chapter (sets resumed_at via segment).
        apiUpdateSegment(self.workout.id, self.workout.segment.id, {
          started_at: self.workout.segment.started_at,
          resumed_at: nowMs(),
          current_user_chapter_id: chapter?.id ?? null,
        });

        self.isPaused = true;
        self.phase = GuidedPhases.PLAYING;
      },

      /** Called by downloadVideo() from the second actions block */
      _setDownloadProgress(progress: number) {
        self.downloadProgress = progress;
      },

      _onDownloadComplete(uri: string, streamed: boolean) {
        self.playbackUri = uri;
        self.isStreaming = streamed;
        self.downloadHandle = null;
        self.downloadProgress = 100;
        self.phase = GuidedPhases.READY;
        console.log(
          `[GuidedStore] Video ready: ${streamed ? 'streaming' : 'cached'}`,
          uri,
        );
      },

      _onDownloadFailed(message: string) {
        // If cancelled, phase is already reset by cancelDownload()
        if (self.phase !== GuidedPhases.IDLE) {
          self.createError = message;
          self.phase = GuidedPhases.IDLE;
        }
        self.downloadHandle = null;
        console.error('[GuidedStore] Video download failed:', message);
      },

      _onStorageFull() {
        self.storageFull = true;
        self.downloadHandle = null;
        if (self.phase !== GuidedPhases.IDLE) {
          self.phase = GuidedPhases.IDLE;
        }
        console.warn(
          '[GuidedStore] Storage full — cannot download workout video',
        );
      },

      acknowledgeStorageFull() {
        self.storageFull = false;
      },

      cancelDownload() {
        if (self.downloadHandle) {
          self.downloadHandle.cancel();
          self.downloadHandle = null;
        }
        self.downloadProgress = 0;
        self.playbackUri = null;
        self.isStreaming = false;
        self.phase = GuidedPhases.IDLE;
        console.log('[GuidedStore] Download cancelled');
      },

      clearVideoCacheState() {
        const hadHandle = !!self.downloadHandle;
        const prevPhase = self.phase;
        if (self.downloadHandle) {
          self.downloadHandle.cancel();
          self.downloadHandle = null;
        }
        self.downloadProgress = 0;
        self.playbackUri = null;
        self.isStreaming = false;
        if (self.phase === GuidedPhases.DOWNLOADING) {
          self.phase = GuidedPhases.IDLE;
        }
        console.log(
          `[GuidedStore] clearVideoCacheState: cancelledInFlight=${hadHandle}, phase ${prevPhase} -> ${self.phase}`,
        );
      },

      cleanup() {
        if (self.downloadHandle) {
          self.downloadHandle.cancel();
          self.downloadHandle = null;
        }
      },

      reset() {
        console.log('[GuidedStore] Resetting store to initial state');
        // Land any pending throttled writes BEFORE clearing self.workout, so an
        // in-flight skip write (e.g. the resume pointer) isn't lost on abandon
        // and can't fire against a torn-down workout. reset() is the single
        // teardown choke point — the (workout) layout calls it on every exit.
        flushBackendWrites();
        if (self.downloadHandle) {
          self.downloadHandle.cancel();
          self.downloadHandle = null;
        }
        self.phase = GuidedPhases.IDLE;
        self.isPaused = false;
        self.observedTimeMs = 0;
        self.seekTargetMs = null;
        self.lastAccountedChapter = 0;
        self.workoutStartedAt = null;
        self.workoutCompletedAt = null;
        self.workout = null;
        self.isCreatingWorkout = false;
        self.createError = null;
        self.downloadProgress = 0;
        self.playbackUri = null;
        self.isStreaming = false;
        self.storageFull = false;
        self.pendingSeekMs = null;
        self.initialSeekMs = null;
      },
    };
  })
  .actions((self) => ({
    downloadVideo: flow(function* () {
      const url = self.videoUrl;
      if (!url) {
        console.warn('[GuidedStore] No video URL to download');
        return;
      }
      const expectedHash = self.videoHash;

      const cachedUri: string | null = yield getCachedVideoUri(url);
      if (cachedUri) {
        const ok: boolean = expectedHash
          ? yield verifyVideoFile(cachedUri, expectedHash)
          : true;
        if (ok) {
          console.log('[GuidedStore] Video already cached, skipping download');
          self._onDownloadComplete(cachedUri, false);
          return;
        }
        console.warn(
          '[GuidedStore] Cached video failed integrity check, redownloading',
        );
        yield deleteCachedVideo(url);
      }

      // Barrier 1: ensure there's room (and evict other cached videos if needed)
      // before committing to the download. No-op on web / when size is unknown.
      const space = yield ensureSpaceForVideo(self.videoSize ?? 0);
      if (space.status === 'insufficient') {
        self._onStorageFull();
        return;
      }

      self.phase = GuidedPhases.DOWNLOADING;
      self.downloadProgress = 0;
      self.playbackUri = null;

      const handle = dlVideo(url, (progress) => {
        self._setDownloadProgress(progress.progress);
      });

      self.downloadHandle = handle;

      handle.promise
        .then(async (result) => {
          if (expectedHash) {
            const ok = await verifyVideoFile(result.uri, expectedHash);
            if (!ok) {
              await deleteCachedVideo(url);
              console.warn(
                '[GuidedStore] Downloaded video failed integrity check',
              );
              self._onDownloadFailed(
                "We couldn't finish preparing your workout. Please try again.",
              );
              return;
            }
          }
          self._onDownloadComplete(result.uri, result.streamed);
        })
        .catch(async (err) => {
          console.error('[GuidedStore] Download error:', err);
          // Clean up any partial file before deciding how to surface the error,
          // so a failed write doesn't keep occupying the space we're short on.
          await deleteCachedVideo(url);
          if (isOutOfSpaceError(err)) {
            self._onStorageFull();
            return;
          }
          self._onDownloadFailed(
            err instanceof Error
              ? err.message
              : "We couldn't finish preparing your workout. Please try again.",
          );
        });
    }),

    loadExistingWorkout(apiWorkout: ApiWorkout) {
      const configData: RemoteConfigData = {
        segments: remoteConfigData.segments as RemoteConfigData['segments'],
      };

      const resolved = resolveGuidedWorkout(apiWorkout, configData);
      self.workout = resolved;
      self.workoutStartedAt =
        resolved.segment.started_at ?? resolved.started_at;
      self.workoutCompletedAt = resolved.completed_at ?? null;

      // Restore chapter index from server-side current_user_chapter_id.
      const currentChapterId = resolved.segment.current_user_chapter_id;
      let chapterIndex = 0;
      if (currentChapterId != null) {
        const idx = resolved.segment.chapters.findIndex(
          (c) => c.id === currentChapterId,
        );
        if (idx >= 0) chapterIndex = idx;
      }
      self.observedTimeMs =
        resolved.segment.chapters[chapterIndex]?.startTimeMs ?? 0;
      self.seekTargetMs = null;
      self.lastAccountedChapter = chapterIndex;

      console.log(
        '[GuidedStore] Loaded existing workout for resume:',
        resolved.id,
        `(chapter ${chapterIndex + 1}/${resolved.chapters.length})`,
      );

      // Kick off video download so pre-workout screen shows the progress.
      (self as any).downloadVideo();
    },

    createAndLoadWorkout: flow(function* (payload: CreateWorkoutPayload) {
      self.isCreatingWorkout = true;
      self.createError = null;

      try {
        const response = yield apiCreateWorkout(payload);

        if (!response.success) {
          self.createError =
            response.error ??
            "We couldn't prepare your workout. Please try again.";
          return;
        }

        const apiWorkout = response.data.workout;
        const configData: RemoteConfigData = {
          segments: remoteConfigData.segments as RemoteConfigData['segments'],
        };

        self.workout = resolveGuidedWorkout(apiWorkout, configData);
        console.log(
          '[GuidedStore] Workout created and loaded:',
          self.workout.id,
          `(${self.workout.chapters.length} chapters)`,
        );

        // Auto-start video download
        (self as any).downloadVideo();
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : "We couldn't prepare your workout. Please try again.";
        self.createError = message;
        console.error('[GuidedStore] createAndLoadWorkout failed:', err);
      } finally {
        self.isCreatingWorkout = false;
      }
    }),
  }));

function formatTime(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

export default GuidedWorkoutPlayerStore;
